import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import Header from '../../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { HelpCircle, MessageSquare, Bug, Plus, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const SupportPage = ({ onMenuClick }) => {
  const [tickets, setTickets] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [category, setCategory] = useState('');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ticketsRes, faqRes] = await Promise.all([
        api.get('/support/tickets'),
        api.get('/support/faq')
      ]);
      setTickets(ticketsRes.data);
      setFaqs(faqRes.data);
    } catch (error) {
      console.error('Error fetching support data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async () => {
    if (!category || !subject.trim() || !description.trim()) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setCreating(true);
    try {
      await api.post('/support/tickets', null, {
        params: { category, subject, description }
      });
      toast.success('Ticket créé avec succès');
      setDialogOpen(false);
      setCategory('');
      setSubject('');
      setDescription('');
      fetchData();
    } catch (error) {
      console.error('Error creating ticket:', error);
      toast.error('Erreur lors de la création du ticket');
    } finally {
      setCreating(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      open: { variant: 'warning', text: 'Ouvert' },
      in_progress: { variant: 'info', text: 'En cours' },
      resolved: { variant: 'success', text: 'Résolu' },
      closed: { variant: 'secondary', text: 'Fermé' }
    };
    const config = variants[status] || variants.open;
    return <Badge variant={config.variant}>{config.text}</Badge>;
  };

  const groupedFaqs = faqs.reduce((acc, faq) => {
    if (!acc[faq.category]) acc[faq.category] = [];
    acc[faq.category].push(faq);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <Header title="Support" onMenuClick={onMenuClick} />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-2">Centre d'Aide</h2>
          <p className="text-slate-600 dark:text-slate-400">Trouvez des réponses ou contactez le support</p>
        </div>

        <Tabs defaultValue="faq" className="space-y-6">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="faq" data-testid="faq-tab">
              <HelpCircle className="w-4 h-4 mr-2" />
              FAQ
            </TabsTrigger>
            <TabsTrigger value="tickets" data-testid="tickets-tab">
              <MessageSquare className="w-4 h-4 mr-2" />
              Mes Tickets ({tickets.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="faq" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Questions Fréquentes</CardTitle>
              </CardHeader>
              <CardContent>
                {Object.keys(groupedFaqs).length > 0 ? (
                  <div className="space-y-6">
                    {Object.entries(groupedFaqs).map(([category, questions]) => (
                      <div key={category}>
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-3">{category}</h3>
                        <div className="space-y-2">
                          {questions.map((faq) => (
                            <div key={faq.id} className="border border-slate-200 dark:border-slate-700 rounded-lg">
                              <button
                                onClick={() => setExpandedFaq(expandedFaq === faq.id ? null : faq.id)}
                                className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                                data-testid={`faq-question-${faq.id}`}
                              >
                                <span className="font-medium text-slate-900 dark:text-white">{faq.question}</span>
                                {expandedFaq === faq.id ? (
                                  <ChevronUp className="w-5 h-5 text-slate-400 flex-shrink-0" />
                                ) : (
                                  <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
                                )}
                              </button>
                              {expandedFaq === faq.id && (
                                <div className="px-4 pb-4 text-sm text-slate-600 dark:text-slate-400">
                                  {faq.answer}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <HelpCircle className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                    <p className="text-slate-600 dark:text-slate-400">Aucune FAQ disponible</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="text-center">
                  <MessageSquare className="w-12 h-12 text-violet-600 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                    Vous ne trouvez pas votre réponse ?
                  </h3>
                  <p className="text-slate-600 dark:text-slate-400 mb-4">
                    Créez un ticket de support et notre équipe vous répondra rapidement
                  </p>
                  <Button onClick={() => setDialogOpen(true)} data-testid="create-ticket-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Créer un ticket
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="tickets" className="space-y-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Mes Tickets de Support</h3>
              <Button onClick={() => setDialogOpen(true)} data-testid="create-ticket-btn-2">
                <Plus className="w-4 h-4 mr-2" />
                Nouveau ticket
              </Button>
            </div>

            {tickets.length > 0 ? (
              <div className="space-y-4">
                {tickets.map((ticket) => (
                  <Card key={ticket.id} data-testid={`ticket-card-${ticket.id}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h4 className="font-semibold text-slate-900 dark:text-white">{ticket.subject}</h4>
                            {getStatusBadge(ticket.status)}
                          </div>
                          <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">{ticket.description}</p>
                          <div className="flex items-center gap-4 text-xs text-slate-500">
                            <span>Catégorie: {ticket.category}</span>
                            <span>•</span>
                            <span>Priorité: {ticket.priority}</span>
                            <span>•</span>
                            <span>{new Date(ticket.created_at).toLocaleDateString('fr-FR')}</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="p-12 text-center">
                  <MessageSquare className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                  <p className="text-slate-600 dark:text-slate-400 mb-4">Aucun ticket créé</p>
                  <Button onClick={() => setDialogOpen(true)}>Créer votre premier ticket</Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Create Ticket Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Nouveau Ticket de Support</DialogTitle>
            <DialogDescription>Décrivez votre problème ou votre question</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="category">Catégorie</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="w-full mt-2" data-testid="category-select">
                  <SelectValue placeholder="Sélectionnez une catégorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pedagogique">Pédagogique</SelectItem>
                  <SelectItem value="administratif">Administratif</SelectItem>
                  <SelectItem value="technique">Technique</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="subject">Sujet</Label>
              <Input
                id="subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Résumez votre demande..."
                className="mt-2"
                data-testid="subject-input"
              />
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Décrivez votre problème en détail..."
                rows={6}
                className="resize-none mt-2"
                data-testid="description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreateTicket} disabled={creating} data-testid="submit-ticket-btn">
              {creating ? 'Création...' : 'Créer le ticket'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupportPage;
